import {
  LegacyClientBlobBucketName,
  LegacyCreateNewBlobHandler,
  LegacyCreateNewBlobRequestDto,
  LegacyStartCreateNewBlobFileDto,
} from "@kaggle/sdk";

export class UploadProgress {
  constructor(initializers?: Partial<UploadProgress>) {
    if (initializers) {
      Object.assign(this, initializers);
    }
  }

  // REVIEW: look at text to see if it can be improved?
  /** User-friendly description of the current state of the upload. */
  description = "Not started";
  // REVIEW: Better names?

  get hasStarted(): boolean {
    return !!this.uploadProgressEvent;
  }

  /** Indicates if the upload is currently sending blob bytes */
  isUploading: boolean = false;

  /** Is the upload currently in a paused state? */
  isPaused: boolean = false;

  /** If present, the date/time when the next retry will occur */
  nextRetryDate?: Date;

  totalRetries?: number;

  previousBytesReceived?: number;

  get totalBytesLoaded(): number {
    return (
      ((this.uploadProgressEvent && this.uploadProgressEvent.loaded) || 0) +
      (this.previousBytesReceived || 0)
    );
  }

  hasSucceeded: boolean = false;
  hasFailed: boolean = false;

  /** Did the upload finish? */
  get isDone(): boolean {
    return this.hasSucceeded || this.hasFailed;
  }

  // HACK: sticking the final progress here (as well as the Promise result) for convenience
  uploadedBlobInfo?: UploadedBlobInfo;

  uploadProgressEvent?: ProgressEvent;

  initialProgressEventTimeStamp?: number;

  get progressProps() {
    return {
      initiatedTimeStamp: this.initialProgressEventTimeStamp as number,
      initialOffsetAmount: this.previousBytesReceived,
      progressEvent: this.uploadProgressEvent,
    };
  }
}

type BlobUploadProgressHandler = (
  progress: UploadProgress
) => void | Promise<void>;

export type UploadResumer = () => UploadPromise;

export interface UploadPromise extends Promise<UploadedBlobInfo> {
  pause: () => UploadResumer;
  progress: UploadProgress;
  onProgress: BlobUploadProgressHandler;
}

export interface UploadedBlobInfo {
  token: string;
  md5Hash: string;
  name: string;
  size: number;
}

// tslint:disable:max-classes-per-file
export class CancellationToken {
  private cancellationCallbacks: (() => void)[] = [];
  // tslint:disable-next-line:naming-convention
  private _isCancellationRequested: boolean = false;

  /**
   * Registers a callback that will be called when the token is cancelled.
   * @param callback to invoke when the token is cancelled.
   * @returns A function that unregisters the callback.
   */
  register(callback: () => void): () => void {
    if (this.isCancellationRequested) {
      callback();
    }

    this.cancellationCallbacks.push(callback);
    return () => {
      // Remove it from the list
      this.cancellationCallbacks = this.cancellationCallbacks.filter(
        cb => cb !== callback
      );
    };
  }

  get isCancellationRequested(): boolean {
    return this._isCancellationRequested;
  }

  // For simplicity, we have a "cancel" method directly on the token (unlike .NET)
  cancel() {
    if (this.isCancellationRequested) {
      // Only invoke once
      return;
    }
    this._isCancellationRequested = true;

    for (const callback of this.cancellationCallbacks) {
      if (callback) {
        callback();
      }
    }
  }
}

// HACK: another idea borrowed from .NET:
class ManualResetEvent {
  // tslint:disable-next-line:no-empty
  private internalResolve: () => void = () => {};
  private internalPromise: Promise<void> = Promise.resolve();

  constructor() {
    this.reset();
  }

  set() {
    if (this.internalResolve) {
      this.internalResolve();
    }
  }

  wait(): Promise<void> {
    return this.internalPromise;
  }

  reset() {
    this.internalPromise = new Promise<void>(resolve => {
      this.internalResolve = resolve;
    });
  }
}

// See https://cloud.google.com/storage/docs/json_api/v1/objects#resource for details
export interface GcsBlobDto {
  generation: number;
  md5Hash: string;
  name: string;
  size: number;
}

// REVIEW: need a better name?
class AsyncPauser {
  isPaused: boolean = false;
  currentCancellationToken: CancellationToken;
  private resumeEvent: ManualResetEvent | null = null;

  constructor() {
    this.currentCancellationToken = new CancellationToken();
  }

  pause(): void {
    if (this.isPaused) {
      return;
    }

    this.isPaused = true;
    this.resumeEvent = new ManualResetEvent();
    this.currentCancellationToken.cancel();
  }

  async waitUntilResumed() {
    if (!this.isPaused || this.resumeEvent === null) {
      return;
    }

    await this.resumeEvent.wait();
  }

  resume() {
    if (!this.isPaused || this.resumeEvent === null) {
      return;
    }
    this.isPaused = false;
    this.currentCancellationToken = new CancellationToken();
    this.resumeEvent.set();
    this.resumeEvent = null;
  }
}

const sleep = async (ms: number) =>
  new Promise(resolve => setTimeout(resolve, ms));

interface ExistingGcsBlobInfo {
  bytesReceived: number;
  completeBlob?: GcsBlobDto;
}

// REVIEW: Should these just be local functions inside the upload closure rather than breaking them out like this?
async function getExistingGcsUploadBytesReceived(
  sessionUrl: string,
  blob: Blob,
  onProgress: BlobUploadProgressHandler
): Promise<ExistingGcsBlobInfo> {
  onProgress(
    new UploadProgress({ description: "Checking server for existing upload" })
  );

  const response = await fetch(sessionUrl, {
    method: "PUT",
    headers: {
      "Content-Range": `bytes */${blob.size}`,
    },
  });

  if (response.status >= 200 && response.status < 300) {
    // Assume we're already done
    // REVIEW: What does the real GCS return?
    return {
      bytesReceived: blob.size,
      completeBlob: (await response.json()) as GcsBlobDto,
    };
  }

  const rangeHeader = response.headers.get("Range");

  if (rangeHeader) {
    // Parse header like "Range: bytes=0-42"
    const rangeMatch = rangeHeader.match(/bytes\s*=\s*([0-9]+)-([0-9]+)/);
    if (!rangeMatch) {
      throw new Error(`Unable to parse Range header: ${rangeHeader}`);
    }
    const startByteOffset = parseInt(rangeMatch[1], 10);

    if (startByteOffset !== 0) {
      throw new Error(`Unexpect Range start offset: ${startByteOffset}`);
    }
    const endByteOffset = parseInt(rangeMatch[2], 10);

    if (endByteOffset >= blob.size) {
      throw new Error(`Range end is larger than expected: ${endByteOffset}`);
    }

    return {
      bytesReceived: endByteOffset + 1,
    };
  }

  return {
    bytesReceived: 0,
  };
}

export function putGcsBlob(
  sessionUrl: string,
  blob: Blob,
  contentType: string,
  attemptResume: boolean,
  onProgress: BlobUploadProgressHandler,
  cancellationToken: CancellationToken
): Promise<GcsBlobDto> {
  // Follow resume protocol outlined in https://cloud.google.com/storage/docs/json_api/v1/how-tos/resumable-upload#resume-upload
  const getExistingBlobInfo: Promise<ExistingGcsBlobInfo> = attemptResume
    ? getExistingGcsUploadBytesReceived(sessionUrl, blob, onProgress)
    : Promise.resolve({ bytesReceived: 0 });

  return getExistingBlobInfo.then(
    (existingInfo: ExistingGcsBlobInfo) =>
      new Promise<GcsBlobDto>((resolve, reject) => {
        if (existingInfo.bytesReceived === blob.size && blob.size > 0) {
          // I guess we're done here.
          // REVIEW: Can we fetch metadata from the sessionUrl?
          resolve(existingInfo.completeBlob);
          return;
        }

        const uploadProgress = new UploadProgress({
          previousBytesReceived: existingInfo.bytesReceived
            ? existingInfo.bytesReceived
            : 0,
        });

        const xhr = new XMLHttpRequest();
        xhr.open("PUT", sessionUrl);
        xhr.upload.onprogress = progressEvent => {
          uploadProgress.description = "Uploading file";
          uploadProgress.isUploading = true;
          uploadProgress.isPaused = false;
          if (!uploadProgress.initialProgressEventTimeStamp) {
            uploadProgress.initialProgressEventTimeStamp =
              progressEvent.timeStamp;
          }
          uploadProgress.uploadProgressEvent = progressEvent;
          onProgress(uploadProgress);
        };

        // REVIEW: Should we listen to the xhr.upload.onerror and onload or the xhr one?
        // Using the xhr one because that at least gives xhr.status
        xhr.onerror = () => {
          uploadProgress.isUploading = false;
          if (cancellationToken && cancellationToken.isCancellationRequested) {
            // Ignore the rejection because the cancel will do that with a different error message
            return;
          }
          reject(xhr.statusText);
        };
        xhr.onload = () => {
          uploadProgress.isUploading = false;

          if (xhr.status >= 200 && xhr.status < 300) {
            uploadProgress.isUploading = false;
            uploadProgress.isPaused = false;
            onProgress(uploadProgress);

            const response = JSON.parse(xhr.responseText) as GcsBlobDto;
            resolve(response);
          } else {
            uploadProgress.description = "Upload was interrupted";
            onProgress(uploadProgress);
            reject(xhr.statusText);
          }
        };

        if (cancellationToken) {
          cancellationToken.register(() => {
            xhr.abort();
            uploadProgress.description = "Upload was paused";
            uploadProgress.isUploading = false;
            uploadProgress.isPaused = false;

            onProgress(uploadProgress);
            reject("Upload was cancelled");
          });
        }

        // Should we do anything special to support additional compression?
        if (existingInfo.bytesReceived > 0) {
          xhr.setRequestHeader(
            "Content-Range",
            `bytes ${existingInfo.bytesReceived}-${blob.size - 1}/${blob.size}`
          );
          xhr.send(
            blob.slice(existingInfo.bytesReceived, blob.size, contentType)
          );
        } else {
          // REVIEW: does this send Content-Type?
          xhr.send(blob);
        }
      })
  );
}

/**
 * Uploads a blob to the specified URL.
 * @param startUrl Server-provided address on where to initiate an upload.
 * @param blob The HTML5 Blob to upload
 * @param name The filename that the blob represents.
 * @param lastModifiedDate The local time when the file was last modified (used to hint timestamp to server).
 * @param onProgress Optional progress handler (suitable for end-user display)
 */
export function uploadBlob(
  createNewBlob: LegacyCreateNewBlobHandler,
  blobBucket: LegacyClientBlobBucketName,
  blob: Blob,
  name: string,
  contentType: string,
  lastModifiedDate?: Date,
  onProgress?: BlobUploadProgressHandler
): UploadPromise {
  const createSession = async (): Promise<LegacyStartCreateNewBlobFileDto> => {
    const requestDto: LegacyCreateNewBlobRequestDto = {
      name,
      contentLength: blob.size,
      contentType,
      lastModifiedDate,
    };

    return createNewBlob({ blobBucket }, requestDto);
  };

  const asyncPauser = new AsyncPauser();

  const internalUpload = async (
    localOnProgress: BlobUploadProgressHandler
  ): Promise<UploadedBlobInfo> => {
    // Fetch the session info from the server
    localOnProgress(new UploadProgress({ description: "Initializing upload" }));

    const sessionInfo = await createSession();

    // We now switch over to GCS
    const maxAttempts = 5000; // setting extremely high to still have _some_ limit (and prevent infinite loop)
    let delaySeconds = 2;

    // Let's hope it makes it on the first attempt, but if not we have maxAttempts to succeed:
    for (let ixAttempt = 0; ixAttempt < maxAttempts; ixAttempt++) {
      try {
        const attemptResume = ixAttempt !== 0;
        const gcsBlobInfo = await putGcsBlob(
          sessionInfo.createUrl,
          blob,
          contentType,
          attemptResume,
          localOnProgress,
          asyncPauser.currentCancellationToken
        );

        let decodedMd5Hash: string | null = null;
        // GCS will return the MD5 Hash as a base64-encoded value. Let's make it more user-readable
        if (gcsBlobInfo.md5Hash) {
          decodedMd5Hash = "";
          // REVIEW: There could be theoretical issues with decoding a base64 string into bytes via a string, but
          // on first glance, it seems OK: https://html.spec.whatwg.org/multipage/webappapis.html#dom-windowbase64-atob
          const rawMd5HashBytes = atob(gcsBlobInfo.md5Hash);

          for (let ixByte = 0; ixByte < rawMd5HashBytes.length; ixByte++) {
            decodedMd5Hash += rawMd5HashBytes.charCodeAt(ixByte).toString(16);
          }
        }

        // Hooray! The blob made it. We're almost done
        const uploadedBlobInfo: UploadedBlobInfo = {
          token: sessionInfo.token,
          md5Hash: decodedMd5Hash as string,
          name,
          size: blob.size,
        };

        // Give one final progress event
        localOnProgress(
          new UploadProgress({
            description: "Upload complete",
            uploadedBlobInfo,
            hasSucceeded: true,
          })
        );

        return uploadedBlobInfo;
      } catch {
        // We should get a cancelled exception, but this is OK
        if (asyncPauser.isPaused) {
          await asyncPauser.waitUntilResumed();
          continue;
        }

        // First retry should happen without delay, then apply exponential backoff.
        // This should (hopefully) gracefully handle a disconnect followed by a reconnect.
        if (ixAttempt > 1) {
          localOnProgress(
            new UploadProgress({
              description: "Error while uploading",
              isPaused: true,
              nextRetryDate: new Date(Date.now() + delaySeconds * 1000),
              totalRetries: ixAttempt,
            })
          );

          await sleep(delaySeconds * 1000);
          delaySeconds = Math.min(delaySeconds * 2, 30);
        }
      }
    }

    // EEK, something bad must have happened to get here
    const failureMessage = `Upload failed, even after ${maxAttempts} attempts`;

    localOnProgress(
      new UploadProgress({
        description: failureMessage,
        isUploading: false,
      })
    );

    throw new Error(failureMessage);
  };

  // REVIEW: this is just here for storing mutable state from the Promise's setters
  // Could possibly be refactored to a container or some other eventing?
  const progressStateContainer = {
    progress: new UploadProgress(),
    onProgress,
  };

  function internalProgressHandler(currentProgress: UploadProgress) {
    progressStateContainer.progress = currentProgress;

    if (progressStateContainer.onProgress) {
      progressStateContainer.onProgress(currentProgress);
    }
  }

  const uploadPromise = internalUpload(internalProgressHandler);

  const result: UploadPromise = Object.defineProperties(uploadPromise, {
    pause: {
      value: () => {
        asyncPauser.pause();
        return () => {
          asyncPauser.resume();
          // We return the same Promise because it was technically never done
          return uploadPromise;
        };
      },
      configurable: false,
      writable: false,
      enumerable: true,
    },
    progress: {
      get: () => {
        return progressStateContainer.progress;
      },
      configurable: false,
      enumerable: true,
    },
    onProgress: {
      get: () => progressStateContainer.onProgress,
      set: op => (progressStateContainer.onProgress = op),
      configurable: false,
      enumerable: true,
    },
  });

  return result;
}

/**
 * Uploads a file to the specified URL.
 * @param startUrl Server-provided address on where to initiate an upload.
 * @param file The HTML5 File to upload
 * @param onProgress Optional progress handler (suitable for end-user display)
 */
export function uploadFile(
  createNewBlob: LegacyCreateNewBlobHandler,
  blobBucket: LegacyClientBlobBucketName,
  file: File,
  onProgress?: BlobUploadProgressHandler
): UploadPromise {
  return uploadBlob(
    createNewBlob,
    blobBucket,
    file,
    file.name,
    file.type,
    new Date(file.lastModified),
    onProgress
  );
}

/** Gets the last modified time of a file in UTC milliseconds */
export function getLastModifiedTimeUTC(file: File): number | null {
  const lastModifiedDateLocal = file.lastModified
    ? new Date(file.lastModified)
    : null;

  return lastModifiedDateLocal
    ? new Date(lastModifiedDateLocal.toUTCString()).getTime()
    : null;
}
